import React, { memo, useCallback } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { BlocoEndereco, FIELDS_END_CJEMP, FIELDS_END_FILIAL, FIELDS_END_PF, FIELDS_END_PFEMP, FIELDS_END_PJ } from "./BlocoEndereco";
import { BlocoTelefone } from "./BlocoTelefone";
import { ChecklistDocumentos } from "./ChecklistDocumentos";
import { CHECKLIST_CONJUGE_LOCATARIO } from "./fichaChecklists";
import { EMAIL_HELPER_TEXT, ESTADOS_CIVIS, ESTADOS_CIVIS_SEM_CONJUGE, REGIMES_CASAMENTO } from "./fichaConstantes";
import { accordionSummarySx, accordionSx, inputSx, selectSx } from "./fichaEstilos";
import { limitarAnoData, mascararCPF, mascararCelular, mascararRG, mascararTelefone, maskMoeda } from "./fichaFormato";
import DocumentoEstrangeiroCampos, { FlagEstrangeiro } from "../DocumentoEstrangeiroCampos";
import { rotuloEstrangeiro } from "../../utils/documentoEstrangeiro";

// Referências estáveis para os fallbacks passados ao ChecklistDocumentos (memo):
// `?? {}` / `?? []` inline criariam objeto novo a cada render e anulariam a memoização.
const EMPTY_DOCS = [];
const EMPTY_UPLOAD_STATE_FICHA = {};

// Formulário de UMA Ficha de Pessoa (PJ ou PF), extraído de pages/FichaCadastro.jsx.
// Não conhece proposta, fluxo de aprovação nem navegação entre abas — tudo o que
// depende disso chega pronto em `checklistAtual` / `mostrarLinkGoogleChecklist`.
export const FichaPessoaForm = memo(function FichaPessoaForm({
  fichaAtual,
  fichaId,
  ro,
  ehAprovador,
  setField,
  errosForm,
  setErrosForm,
  buscandoCnpj,
  handleCnpjChange,
  primeiroCampoRef,
  checklistAtual,
  docsPorFicha,
  uploadState,
  uploadando,
  mostrarLinkGoogleChecklist,
  linkGoogleImovel,
  setUploadField,
  handleUploadDoc,
  handleDeleteDoc,
  handleVisualizarDoc,
  handleDownloadDoc,
}) {
  const campoVazio = (v) => ehAprovador && (v === null || v === undefined || v === "");

  // Vermelho já na visualização
  const erroEfetivo = useCallback(
    (campo, valor) => {
      const vazio = valor === null || valor === undefined || (typeof valor === "string" && !valor.trim());

      if (!vazio) {
        return undefined;
      }

      return errosForm[campo] || "Campo obrigatório não preenchido.";
    },
    [errosForm],
  );

  // Estrangeiro: CPF/CNPJ opcionais; sem CPF/CNPJ, o documento estrangeiro é obrigatório.
  const docOpcional = Boolean(fichaAtual.estrangeiro);
  const erroDocumento = (campo) => (docOpcional ? undefined : erroEfetivo(campo, fichaAtual[campo]));
  const alterarEstrangeiro = (campo, valor) => {
    setField(campo, valor);
    setErrosForm((p) => ({ ...p, [campo]: undefined }));
  };
  // Interruptor ao lado do CPF/CNPJ (ex.: "Locatário estrangeiro", "Fiador estrangeiro").
  const flagEstrangeiro = (
    <Grid size={{ xs: 12, md: "auto" }} sx={{ display: "flex", alignItems: "center" }}>
      <FlagEstrangeiro valor={fichaAtual} onChange={alterarEstrangeiro} somenteLeitura={ro} rotulo={rotuloEstrangeiro(fichaAtual.campoProposta)} />
    </Grid>
  );
  const blocoEstrangeiro = fichaAtual.estrangeiro && (
    <Grid size={{ xs: 12 }}>
      <DocumentoEstrangeiroCampos
        obrigatorio={!(fichaAtual.tipoPessoa === "PJ" ? fichaAtual.cnpj : fichaAtual.cpf)}
        mostrarFlag={false}
        valor={fichaAtual}
        onChange={alterarEstrangeiro}
        somenteLeitura={ro}
        erros={errosForm}
        campoSx={(campo) => inputSx(ro, false, !!errosForm[campo])}
      />
    </Grid>
  );

  // ── Seção PJ ─────────────────────────────────────────────────────────────

  const renderFormPJ = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Dados Cadastrais */}
      <Accordion defaultExpanded sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Dados Cadastrais</Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            {/* CNPJ */}
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label={docOpcional ? "CNPJ (opcional)" : "CNPJ*"}
                size="small"
                fullWidth
                value={fichaAtual.cnpj}
                placeholder="00.000.000/0000-00"
                onChange={(e) => !ro && handleCnpjChange(e.target.value)}
                onBlur={() => {
                  if (!ro && !docOpcional && !fichaAtual.cnpj?.trim()) {
                    setErrosForm((p) => ({
                      ...p,
                      cnpj: "CNPJ é obrigatório.",
                    }));
                  }
                }}
                error={!!erroDocumento("cnpj")}
                helperText={erroDocumento("cnpj") || ""}
                slotProps={{
                  input: {
                    readOnly: ro,
                    endAdornment: buscandoCnpj ? (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ) : null,
                  },
                  htmlInput: {
                    maxLength: 18,
                  },
                }}
                sx={inputSx(ro, campoVazio(fichaAtual.cnpj), !!erroDocumento("cnpj"))}
              />
            </Grid>

            {flagEstrangeiro}
            {blocoEstrangeiro}

            {/* Razão Social */}
            <Grid size={{ xs: 12, md: 9 }}>
              <TextField
                label="Razão Social*"
                size="small"
                fullWidth
                inputRef={primeiroCampoRef}
                value={fichaAtual.razaoSocial}
                onChange={(e) => !ro && setField("razaoSocial", e.target.value.toUpperCase())}
                onBlur={() => {
                  if (!ro && !fichaAtual.razaoSocial?.trim()) {
                    setErrosForm((p) => ({
                      ...p,
                      razaoSocial: "Razão Social é obrigatória.",
                    }));
                  }
                }}
                error={!!erroEfetivo("razaoSocial", fichaAtual.razaoSocial)}
                helperText={erroEfetivo("razaoSocial", fichaAtual.razaoSocial) || ""}
                slotProps={{
                  input: {
                    readOnly: ro,
                  },
                  htmlInput: {
                    maxLength: 255,
                  },
                }}
                sx={{
                  ...inputSx(ro, campoVazio(fichaAtual.razaoSocial), !!erroEfetivo("razaoSocial", fichaAtual.razaoSocial)),
                  "& input": {
                    color: "var(--ig-t-ffffff)",
                    textTransform: "uppercase",
                  },
                }}
              />
            </Grid>

            {/* Objeto Social Principal */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Objeto Social Principal"
                size="small"
                fullWidth
                value={fichaAtual.objetoSocialPrincipal}
                onChange={(e) => !ro && setField("objetoSocialPrincipal", e.target.value.toUpperCase())}
                slotProps={{
                  input: {
                    readOnly: ro,
                  },
                }}
                sx={{
                  ...inputSx(ro, campoVazio(fichaAtual.objetoSocialPrincipal)),
                  "& input": {
                    color: "var(--ig-t-ffffff)",
                    textTransform: "uppercase",
                  },
                }}
              />
            </Grid>

            {/* Outros Objetos Sociais */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Outros Objetos Sociais"
                size="small"
                fullWidth
                value={fichaAtual.outrosObjetosSociais}
                onChange={(e) => !ro && setField("outrosObjetosSociais", e.target.value.toUpperCase())}
                slotProps={{
                  input: {
                    readOnly: ro,
                  },
                }}
                sx={{
                  ...inputSx(ro, campoVazio(fichaAtual.outrosObjetosSociais)),
                  "& input": {
                    color: "var(--ig-t-ffffff)",
                    textTransform: "uppercase",
                  },
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Endereço Principal */}
      <Accordion defaultExpanded sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Endereço Principal</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <BlocoEndereco fields={FIELDS_END_PJ} form={fichaAtual} setField={setField} ro={ro} ehAprovador={ehAprovador} layout="largo" />
          <Box sx={{ mt: 2 }}>
            <BlocoTelefone
              prefixTel="telefone"
              prefixCel="celular"
              form={fichaAtual}
              setField={setField}
              ro={ro}
              ehAprovador={ehAprovador}
              labelTel="Telefone Comercial"
            />
          </Box>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={12}>
              <TextField
                label="E-mail*"
                size="small"
                fullWidth
                helperText={erroEfetivo("email", fichaAtual.email) || EMAIL_HELPER_TEXT}
                error={!!erroEfetivo("email", fichaAtual.email)}
                value={fichaAtual.email}
                onChange={(e) => !ro && setField("email", e.target.value)}
                onBlur={() => {
                  if (!ro && !fichaAtual.email?.trim()) setErrosForm((p) => ({ ...p, email: "E-mail é obrigatório." }));
                }}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 500 } }}
                sx={inputSx(ro, campoVazio(fichaAtual.email), !!erroEfetivo("email", fichaAtual.email))}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Dados Empresariais */}
      <Accordion defaultExpanded sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Dados Empresariais</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label="Data de Constituição"
                type="date"
                size="small"
                fullWidth
                value={fichaAtual.dataConstituicao}
                onChange={(e) => !ro && setField("dataConstituicao", limitarAnoData(e.target.value))}
                slotProps={{ input: { readOnly: ro }, inputLabel: { shrink: true } }}
                sx={inputSx(ro, campoVazio(fichaAtual.dataConstituicao))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label="Capital Social (R$)"
                size="small"
                fullWidth
                value={fichaAtual.capitalSocial}
                onChange={(e) => !ro && setField("capitalSocial", maskMoeda(e.target.value))}
                slotProps={{ input: { readOnly: ro } }}
                sx={inputSx(ro, campoVazio(fichaAtual.capitalSocial))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label="Faturamento Mensal (R$)"
                size="small"
                fullWidth
                value={fichaAtual.faturamentoMensal}
                onChange={(e) => !ro && setField("faturamentoMensal", maskMoeda(e.target.value))}
                slotProps={{ input: { readOnly: ro } }}
                sx={inputSx(ro, campoVazio(fichaAtual.faturamentoMensal))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label="Qtde. Funcionários"
                size="small"
                fullWidth
                value={fichaAtual.qtdeFuncionarios}
                onChange={(e) => !ro && setField("qtdeFuncionarios", e.target.value.toUpperCase())}
                slotProps={{ input: { readOnly: ro } }}
                sx={{ ...inputSx(ro, campoVazio(fichaAtual.qtdeFuncionarios)), "& input": { color: "var(--ig-t-ffffff)", textTransform: "uppercase" } }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Ramo de Atividade"
                size="small"
                fullWidth
                value={fichaAtual.ramoAtividade}
                onChange={(e) => !ro && setField("ramoAtividade", e.target.value.toUpperCase())}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 100 } }}
                sx={{ ...inputSx(ro, campoVazio(fichaAtual.ramoAtividade)), "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" } }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Produtos / Serviços"
                size="small"
                fullWidth
                multiline
                rows={3}
                value={fichaAtual.produtosServicos}
                onChange={(e) => !ro && setField("produtosServicos", e.target.value.toUpperCase())}
                slotProps={{ input: { readOnly: ro } }}
                sx={{ ...inputSx(ro, campoVazio(fichaAtual.produtosServicos)), "& textarea": { color: "var(--ig-t-ffffff)", textTransform: "uppercase" } }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Endereço Filial */}
      <Accordion sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Endereço da Filial</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <BlocoEndereco fields={FIELDS_END_FILIAL} form={fichaAtual} setField={setField} ro={ro} ehAprovador={ehAprovador} layout="largo" />
          <Box sx={{ mt: 2 }}>
            <BlocoTelefone prefixTel="telefoneFilial" prefixCel="celularFilial" form={fichaAtual} setField={setField} ro={ro} ehAprovador={ehAprovador} />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Representante Legal — reaproveita os campos pf*/}
      <Accordion sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>
            Dados Cadastrais do Representante (que irá assinar o contrato de locação)
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                label="Nome Completo do Representante*"
                size="small"
                fullWidth
                value={fichaAtual.pfNome}
                onChange={(e) => {
                  if (!ro) {
                    setField("pfNome", e.target.value.toUpperCase());
                    setErrosForm((p) => ({ ...p, pfNome: undefined }));
                  }
                }}
                onBlur={() => {
                  if (!ro && !fichaAtual.pfNome?.trim()) setErrosForm((p) => ({ ...p, pfNome: "Nome do Representante é obrigatório." }));
                }}
                error={!!erroEfetivo("pfNome", fichaAtual.pfNome)}
                helperText={erroEfetivo("pfNome", fichaAtual.pfNome) || ""}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 255 } }}
                sx={{
                  ...inputSx(ro, campoVazio(fichaAtual.pfNome), !!erroEfetivo("pfNome", fichaAtual.pfNome)),
                  "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Data de Nascimento"
                type="date"
                size="small"
                fullWidth
                value={fichaAtual.pfDtNascimento}
                onChange={(e) => !ro && setField("pfDtNascimento", limitarAnoData(e.target.value))}
                slotProps={{ input: { readOnly: ro }, inputLabel: { shrink: true } }}
                sx={inputSx(ro, campoVazio(fichaAtual.pfDtNascimento))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <FormControl size="small" fullWidth>
                <InputLabel shrink sx={{ color: "var(--ig-t-aaaaaa)", fontWeight: "bold" }}>
                  Estado Civil
                </InputLabel>
                <Select
                  displayEmpty
                  notched
                  label="Estado Civil"
                  value={fichaAtual.pfEstadoCivil}
                  onChange={(e) => !ro && setField("pfEstadoCivil", e.target.value)}
                  inputProps={{ readOnly: ro }}
                  renderValue={(v) => v || <em style={{ color: "var(--ig-t-666666)", fontStyle: "italic" }}>Selecione</em>}
                  sx={selectSx(ro, campoVazio(fichaAtual.pfEstadoCivil))}
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  {ESTADOS_CIVIS.map((ec) => (
                    <MenuItem key={ec} value={ec}>
                      {ec}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {fichaAtual.pfEstadoCivil === "CASADO(A)" && (
              <Grid size={12}>
                <FormControl size="small" fullWidth>
                  <InputLabel shrink sx={{ color: "var(--ig-t-aaaaaa)", fontWeight: "bold" }}>
                    Regime de Casamento
                  </InputLabel>
                  <Select
                    displayEmpty
                    notched
                    label="Regime de Casamento"
                    value={fichaAtual.pfRegimeCasamento}
                    onChange={(e) => !ro && setField("pfRegimeCasamento", e.target.value)}
                    inputProps={{ readOnly: ro }}
                    renderValue={(v) => v || <em style={{ color: "var(--ig-t-666666)", fontStyle: "italic" }}>Selecione</em>}
                    sx={selectSx(ro, campoVazio(fichaAtual.pfRegimeCasamento))}
                  >
                    <MenuItem value="">
                      <em>Selecione</em>
                    </MenuItem>
                    {REGIMES_CASAMENTO.map((rc) => (
                      <MenuItem key={rc} value={rc}>
                        {rc}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Nacionalidade"
                size="small"
                fullWidth
                value={fichaAtual.pfNacionalidade}
                onChange={(e) => !ro && setField("pfNacionalidade", e.target.value.toUpperCase())}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 50 } }}
                sx={{ ...inputSx(ro, campoVazio(fichaAtual.pfNacionalidade)), "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Naturalidade"
                size="small"
                fullWidth
                value={fichaAtual.pfNaturalidade}
                onChange={(e) => !ro && setField("pfNaturalidade", e.target.value.toUpperCase())}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 50 } }}
                sx={{ ...inputSx(ro, campoVazio(fichaAtual.pfNaturalidade)), "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Profissão"
                size="small"
                fullWidth
                value={fichaAtual.pfProfissao}
                onChange={(e) => !ro && setField("pfProfissao", e.target.value.toUpperCase())}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 30 } }}
                sx={{ ...inputSx(ro, campoVazio(fichaAtual.pfProfissao)), "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label={docOpcional ? "CPF do Representante (opcional)" : "CPF do Representante*"}
                size="small"
                fullWidth
                value={fichaAtual.cpf}
                placeholder="000.000.000-00"
                onChange={(e) => {
                  if (!ro) {
                    setField("cpf", mascararCPF(e.target.value));
                    setErrosForm((p) => ({ ...p, cpf: undefined }));
                  }
                }}
                onBlur={() => {
                  if (!ro && !docOpcional && !fichaAtual.cpf?.trim()) setErrosForm((p) => ({ ...p, cpf: "CPF do Representante é obrigatório." }));
                }}
                error={!!erroDocumento("cpf")}
                helperText={erroDocumento("cpf") || ""}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 18 } }}
                sx={inputSx(ro, campoVazio(fichaAtual.cpf), !!erroDocumento("cpf"))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="RG"
                size="small"
                fullWidth
                value={fichaAtual.rg}
                placeholder="00.000.000-0"
                onChange={(e) => !ro && setField("rg", mascararRG(e.target.value))}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 12 } }}
                sx={inputSx(ro, campoVazio(fichaAtual.rg))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Órgão Emissor do RG"
                size="small"
                fullWidth
                value={fichaAtual.rgOrgao}
                onChange={(e) => !ro && setField("rgOrgao", e.target.value.toUpperCase())}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 30 } }}
                sx={{ ...inputSx(ro, campoVazio(fichaAtual.rgOrgao)), "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Data de Emissão RG"
                type="date"
                size="small"
                fullWidth
                value={fichaAtual.rgEmissao}
                onChange={(e) => !ro && setField("rgEmissao", limitarAnoData(e.target.value))}
                slotProps={{ input: { readOnly: ro }, inputLabel: { shrink: true } }}
                sx={inputSx(ro, campoVazio(fichaAtual.rgEmissao))}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <BlocoTelefone
              prefixTel="pfTelefone"
              prefixCel="pfCelular"
              form={fichaAtual}
              setField={setField}
              ro={ro}
              ehAprovador={ehAprovador}
              labelTel="Telefone Comercial"
            />
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid size={12}>
                <TextField
                  label="E-mail"
                  size="small"
                  fullWidth
                  helperText={EMAIL_HELPER_TEXT}
                  value={fichaAtual.pfEmail}
                  onChange={(e) => !ro && setField("pfEmail", e.target.value)}
                  slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 500 } }}
                  sx={inputSx(ro, campoVazio(fichaAtual.pfEmail))}
                />
              </Grid>
            </Grid>
          </Box>

          {fichaAtual.campoProposta === "Locataria" && fichaAtual.pfEstadoCivil && !ESTADOS_CIVIS_SEM_CONJUGE.includes(fichaAtual.pfEstadoCivil) && (
            <>
              <Divider sx={{ my: 2, borderColor: "var(--ig-b-333333)" }} />
              <Typography sx={{ color: "var(--ig-t-bbbbbb)", fontWeight: 600, fontSize: "0.85rem", mb: 1 }}>Cônjuge do Representante</Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField
                    label="Nome do Cônjuge"
                    size="small"
                    fullWidth
                    value={fichaAtual.pfConjuge}
                    onChange={(e) => !ro && setField("pfConjuge", e.target.value.toUpperCase())}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 255 } }}
                    sx={{ ...inputSx(ro, campoVazio(fichaAtual.pfConjuge)), "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="Data de Nascimento"
                    type="date"
                    size="small"
                    fullWidth
                    value={fichaAtual.pfConjugeDtNascimento}
                    onChange={(e) => !ro && setField("pfConjugeDtNascimento", limitarAnoData(e.target.value))}
                    slotProps={{ input: { readOnly: ro }, inputLabel: { shrink: true } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.pfConjugeDtNascimento))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="Profissão"
                    size="small"
                    fullWidth
                    value={fichaAtual.pfConjugeProfissao}
                    onChange={(e) => !ro && setField("pfConjugeProfissao", e.target.value.toUpperCase())}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 30 } }}
                    sx={{ ...inputSx(ro, campoVazio(fichaAtual.pfConjugeProfissao)), "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="Nacionalidade"
                    size="small"
                    fullWidth
                    value={fichaAtual.pfConjugeNacionalidade}
                    onChange={(e) => !ro && setField("pfConjugeNacionalidade", e.target.value.toUpperCase())}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 50 } }}
                    sx={{
                      ...inputSx(ro, campoVazio(fichaAtual.pfConjugeNacionalidade)),
                      "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="Naturalidade"
                    size="small"
                    fullWidth
                    value={fichaAtual.pfConjugeNaturalidade}
                    onChange={(e) => !ro && setField("pfConjugeNaturalidade", e.target.value.toUpperCase())}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 50 } }}
                    sx={{
                      ...inputSx(ro, campoVazio(fichaAtual.pfConjugeNaturalidade)),
                      "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="CPF do Cônjuge"
                    size="small"
                    fullWidth
                    value={fichaAtual.conjugeCpf}
                    placeholder="000.000.000-00"
                    onChange={(e) => !ro && setField("conjugeCpf", mascararCPF(e.target.value))}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 18 } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.conjugeCpf))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="RG do Cônjuge"
                    size="small"
                    fullWidth
                    value={fichaAtual.conjugeRg}
                    placeholder="00.000.000-0"
                    onChange={(e) => !ro && setField("conjugeRg", mascararRG(e.target.value))}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 12 } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.conjugeRg))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="Órgão Emissor do RG"
                    size="small"
                    fullWidth
                    value={fichaAtual.conjugeRgOrgao}
                    onChange={(e) => !ro && setField("conjugeRgOrgao", e.target.value.toUpperCase())}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 30 } }}
                    sx={{ ...inputSx(ro, campoVazio(fichaAtual.conjugeRgOrgao)), "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="Data de Emissão RG"
                    type="date"
                    size="small"
                    fullWidth
                    value={fichaAtual.conjugeRgEmissao}
                    onChange={(e) => !ro && setField("conjugeRgEmissao", limitarAnoData(e.target.value))}
                    slotProps={{ input: { readOnly: ro }, inputLabel: { shrink: true } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.conjugeRgEmissao))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Telefone Comercial"
                    size="small"
                    fullWidth
                    value={fichaAtual.conjugeTelefone}
                    placeholder="(00) 0000-0000"
                    onChange={(e) => !ro && setField("conjugeTelefone", mascararTelefone(e.target.value))}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 15 } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.conjugeTelefone))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Celular"
                    size="small"
                    fullWidth
                    value={fichaAtual.conjugeCelular}
                    placeholder="(00) 00000-0000"
                    onChange={(e) => !ro && setField("conjugeCelular", mascararCelular(e.target.value))}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 15 } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.conjugeCelular))}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    label="E-mail"
                    size="small"
                    fullWidth
                    helperText={EMAIL_HELPER_TEXT}
                    value={fichaAtual.conjugeEmail}
                    onChange={(e) => !ro && setField("conjugeEmail", e.target.value)}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 500 } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.conjugeEmail))}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Checklist de Documentos PJ */}
      <Accordion sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Checklist de Documentos</Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 1 }}>
          <ChecklistDocumentos
            checklist={checklistAtual}
            fichaId={fichaId}
            docs={docsPorFicha[fichaId] ?? EMPTY_DOCS}
            uploadState={uploadState[fichaId] ?? EMPTY_UPLOAD_STATE_FICHA}
            uploadando={uploadando}
            ro={ro}

            mostrarLinkGoogle={mostrarLinkGoogleChecklist}
            linkGoogleImovel={linkGoogleImovel}
            onChangeLinkGoogleImovel={(valor) => setField("linkGoogleImovel", valor)}

            observacaoDocumental={fichaAtual.observacaoDocumental ?? ""}
            onChangeObservacaoDocumental={(valor) => setField("observacaoDocumental", valor)}

            onSetUploadField={setUploadField}
            onUpload={handleUploadDoc}
            onDelete={handleDeleteDoc}
            onVisualizar={handleVisualizarDoc}
            onDownload={handleDownloadDoc}
          />
        </AccordionDetails>
      </Accordion>

      {/* Observação */}
      <Accordion sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Observação</Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 2 }}>
          <TextField
            label="Observação"
            size="small"
            fullWidth
            multiline
            rows={4}
            value={fichaAtual.observacao ?? ""}
            onChange={(e) => !ro && setField("observacao", e.target.value.toUpperCase())}
            slotProps={{
              input: {
                readOnly: ro,
              },
            }}
            sx={{
              ...inputSx(ro, campoVazio(fichaAtual.observacao)),
              "& textarea": {
                color: "var(--ig-t-ffffff)",
                textTransform: "uppercase",
              },
            }}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  // ── Seção PF ─────────────────────────────────────────────────────────────

  const renderFormPF = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Dados Pessoais */}
      <Accordion defaultExpanded sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Dados Pessoais e Documentos</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                label="Nome Completo*"
                size="small"
                fullWidth
                inputRef={primeiroCampoRef}
                value={fichaAtual.pfNome}
                onChange={(e) => !ro && setField("pfNome", e.target.value.toUpperCase())}
                onBlur={() => {
                  if (!ro && !fichaAtual.pfNome?.trim()) setErrosForm((p) => ({ ...p, pfNome: "Nome Completo é obrigatório." }));
                }}
                error={!!erroEfetivo("pfNome", fichaAtual.pfNome)}
                helperText={erroEfetivo("pfNome", fichaAtual.pfNome) || ""}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 255 } }}
                sx={{
                  ...inputSx(ro, campoVazio(fichaAtual.pfNome), !!erroEfetivo("pfNome", fichaAtual.pfNome)),
                  "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Data de Nascimento*"
                type="date"
                size="small"
                fullWidth
                value={fichaAtual.pfDtNascimento}
                onChange={(e) => !ro && setField("pfDtNascimento", limitarAnoData(e.target.value))}
                onBlur={() => {
                  if (!ro && !fichaAtual.pfDtNascimento) setErrosForm((p) => ({ ...p, pfDtNascimento: "Data de Nascimento é obrigatória." }));
                }}
                error={!!erroEfetivo("pfDtNascimento", fichaAtual.pfDtNascimento)}
                helperText={erroEfetivo("pfDtNascimento", fichaAtual.pfDtNascimento) || ""}
                slotProps={{ input: { readOnly: ro }, inputLabel: { shrink: true } }}
                sx={inputSx(ro, campoVazio(fichaAtual.pfDtNascimento), !!erroEfetivo("pfDtNascimento", fichaAtual.pfDtNascimento))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <FormControl size="small" fullWidth error={!!erroEfetivo("pfEstadoCivil", fichaAtual.pfEstadoCivil)}>
                <InputLabel
                  shrink
                  sx={{ color: erroEfetivo("pfEstadoCivil", fichaAtual.pfEstadoCivil) ? "#f44336" : "var(--ig-t-aaaaaa)", fontWeight: "bold" }}
                >
                  Estado Civil*
                </InputLabel>
                <Select
                  displayEmpty
                  notched
                  label="Estado Civil*"
                  value={fichaAtual.pfEstadoCivil}
                  onChange={(e) => {
                    if (!ro) {
                      setField("pfEstadoCivil", e.target.value);
                      setErrosForm((p) => ({ ...p, pfEstadoCivil: undefined }));
                    }
                  }}
                  onBlur={() => {
                    if (!ro && !fichaAtual.pfEstadoCivil) setErrosForm((p) => ({ ...p, pfEstadoCivil: "Estado Civil é obrigatório." }));
                  }}
                  inputProps={{ readOnly: ro }}
                  renderValue={(v) => v || <em style={{ color: "var(--ig-t-666666)", fontStyle: "italic" }}>Selecione</em>}
                  sx={selectSx(ro, campoVazio(fichaAtual.pfEstadoCivil))}
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  {ESTADOS_CIVIS.map((ec) => (
                    <MenuItem key={ec} value={ec}>
                      {ec}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{erroEfetivo("pfEstadoCivil", fichaAtual.pfEstadoCivil) || ""}</FormHelperText>
              </FormControl>
            </Grid>
            {fichaAtual.pfEstadoCivil === "CASADO(A)" && (
              <Grid size={12}>
                <FormControl size="small" fullWidth>
                  <InputLabel shrink sx={{ color: "var(--ig-t-aaaaaa)", fontWeight: "bold" }}>
                    Regime de Casamento
                  </InputLabel>
                  <Select
                    displayEmpty
                    notched
                    label="Regime de Casamento"
                    value={fichaAtual.pfRegimeCasamento}
                    onChange={(e) => !ro && setField("pfRegimeCasamento", e.target.value)}
                    inputProps={{ readOnly: ro }}
                    renderValue={(v) => v || <em style={{ color: "var(--ig-t-666666)", fontStyle: "italic" }}>Selecione</em>}
                    sx={selectSx(ro, campoVazio(fichaAtual.pfRegimeCasamento))}
                  >
                    <MenuItem value="">
                      <em>Selecione</em>
                    </MenuItem>
                    {REGIMES_CASAMENTO.map((rc) => (
                      <MenuItem key={rc} value={rc}>
                        {rc}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Nacionalidade"
                size="small"
                fullWidth
                value={fichaAtual.pfNacionalidade}
                onChange={(e) => !ro && setField("pfNacionalidade", e.target.value.toUpperCase())}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 50 } }}
                sx={{ ...inputSx(ro, campoVazio(fichaAtual.pfNacionalidade)), "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Naturalidade"
                size="small"
                fullWidth
                value={fichaAtual.pfNaturalidade}
                onChange={(e) => !ro && setField("pfNaturalidade", e.target.value.toUpperCase())}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 50 } }}
                sx={{ ...inputSx(ro, campoVazio(fichaAtual.pfNaturalidade)), "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Profissão"
                size="small"
                fullWidth
                value={fichaAtual.pfProfissao}
                onChange={(e) => !ro && setField("pfProfissao", e.target.value.toUpperCase())}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 30 } }}
                sx={{ ...inputSx(ro, campoVazio(fichaAtual.pfProfissao)), "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label={docOpcional ? "CPF (opcional)" : "CPF*"}
                size="small"
                fullWidth
                value={fichaAtual.cpf}
                placeholder="000.000.000-00"
                onChange={(e) => !ro && setField("cpf", mascararCPF(e.target.value))}
                onBlur={() => {
                  if (!ro && !docOpcional && !fichaAtual.cpf?.trim()) setErrosForm((p) => ({ ...p, cpf: "CPF é obrigatório." }));
                }}
                error={!!erroDocumento("cpf")}
                helperText={erroDocumento("cpf") || ""}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 18 } }}
                sx={inputSx(ro, campoVazio(fichaAtual.cpf), !!erroDocumento("cpf"))}
              />
            </Grid>
            {flagEstrangeiro}
            {blocoEstrangeiro}
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="RG"
                size="small"
                fullWidth
                value={fichaAtual.rg}
                placeholder="00.000.000-0"
                onChange={(e) => !ro && setField("rg", mascararRG(e.target.value))}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 12 } }}
                sx={inputSx(ro, campoVazio(fichaAtual.rg))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Órgão Emissor do RG"
                size="small"
                fullWidth
                value={fichaAtual.rgOrgao}
                onChange={(e) => !ro && setField("rgOrgao", e.target.value.toUpperCase())}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 30 } }}
                sx={{ ...inputSx(ro, campoVazio(fichaAtual.rgOrgao)), "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Data de Emissão RG"
                type="date"
                size="small"
                fullWidth
                value={fichaAtual.rgEmissao}
                onChange={(e) => !ro && setField("rgEmissao", limitarAnoData(e.target.value))}
                slotProps={{ input: { readOnly: ro }, inputLabel: { shrink: true } }}
                sx={inputSx(ro, campoVazio(fichaAtual.rgEmissao))}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Endereço */}
      <Accordion defaultExpanded sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Endereço e Contato</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <BlocoEndereco
            fields={FIELDS_END_PF}
            form={fichaAtual}
            setField={setField}
            ro={ro}
            ehAprovador={ehAprovador}
            erroEfetivo={erroEfetivo}
            setErrosForm={setErrosForm}
            layout="largo"
          />
          <Box sx={{ mt: 2 }}>
            <BlocoTelefone
              prefixTel="pfTelefone"
              prefixCel="pfCelular"
              form={fichaAtual}
              setField={setField}
              ro={ro}
              ehAprovador={ehAprovador}
              labelTel="Telefone Residencial"
            />
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid size={12}>
                <TextField
                  label="E-mail"
                  size="small"
                  fullWidth
                  helperText={EMAIL_HELPER_TEXT}
                  value={fichaAtual.pfEmail}
                  onChange={(e) => !ro && setField("pfEmail", e.target.value)}
                  slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 500 } }}
                  sx={inputSx(ro, campoVazio(fichaAtual.pfEmail), false)}
                />
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>

      {fichaAtual.pfEstadoCivil && !ESTADOS_CIVIS_SEM_CONJUGE.includes(fichaAtual.pfEstadoCivil) && (
        <>
          {/* Cônjuge */}
          <Accordion sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
              <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Cônjuge</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField
                    label="Nome do Cônjuge"
                    size="small"
                    fullWidth
                    value={fichaAtual.pfConjuge}
                    onChange={(e) => !ro && setField("pfConjuge", e.target.value.toUpperCase())}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 255 } }}
                    sx={{
                      ...inputSx(ro, campoVazio(fichaAtual.pfConjuge), false),
                      "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="Data de Nascimento"
                    type="date"
                    size="small"
                    fullWidth
                    value={fichaAtual.pfConjugeDtNascimento}
                    onChange={(e) => !ro && setField("pfConjugeDtNascimento", limitarAnoData(e.target.value))}
                    slotProps={{ input: { readOnly: ro }, inputLabel: { shrink: true } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.pfConjugeDtNascimento), false)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="Profissão"
                    size="small"
                    fullWidth
                    value={fichaAtual.pfConjugeProfissao}
                    onChange={(e) => !ro && setField("pfConjugeProfissao", e.target.value.toUpperCase())}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 30 } }}
                    sx={{
                      ...inputSx(ro, campoVazio(fichaAtual.pfConjugeProfissao), false),
                      "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="Nacionalidade"
                    size="small"
                    fullWidth
                    value={fichaAtual.pfConjugeNacionalidade}
                    onChange={(e) => !ro && setField("pfConjugeNacionalidade", e.target.value.toUpperCase())}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 50 } }}
                    sx={{
                      ...inputSx(ro, campoVazio(fichaAtual.pfConjugeNacionalidade)),
                      "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="Naturalidade"
                    size="small"
                    fullWidth
                    value={fichaAtual.pfConjugeNaturalidade}
                    onChange={(e) => !ro && setField("pfConjugeNaturalidade", e.target.value.toUpperCase())}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 50 } }}
                    sx={{
                      ...inputSx(ro, campoVazio(fichaAtual.pfConjugeNaturalidade)),
                      "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="CPF do Cônjuge"
                    size="small"
                    fullWidth
                    value={fichaAtual.conjugeCpf}
                    placeholder="000.000.000-00"
                    onChange={(e) => !ro && setField("conjugeCpf", mascararCPF(e.target.value))}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 18 } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.conjugeCpf), false)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="RG do Cônjuge"
                    size="small"
                    fullWidth
                    value={fichaAtual.conjugeRg}
                    placeholder="00.000.000-0"
                    onChange={(e) => !ro && setField("conjugeRg", mascararRG(e.target.value))}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 12 } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.conjugeRg), false)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="Órgão Emissor do RG"
                    size="small"
                    fullWidth
                    value={fichaAtual.conjugeRgOrgao}
                    onChange={(e) => !ro && setField("conjugeRgOrgao", e.target.value.toUpperCase())}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 30 } }}
                    sx={{ ...inputSx(ro, campoVazio(fichaAtual.conjugeRgOrgao)), "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="Data de Emissão RG"
                    type="date"
                    size="small"
                    fullWidth
                    value={fichaAtual.conjugeRgEmissao}
                    onChange={(e) => !ro && setField("conjugeRgEmissao", limitarAnoData(e.target.value))}
                    slotProps={{ input: { readOnly: ro }, inputLabel: { shrink: true } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.conjugeRgEmissao))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Telefone Comercial"
                    size="small"
                    fullWidth
                    value={fichaAtual.conjugeTelefone}
                    placeholder="(00) 0000-0000"
                    onChange={(e) => !ro && setField("conjugeTelefone", mascararTelefone(e.target.value))}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 15 } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.conjugeTelefone))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Celular"
                    size="small"
                    fullWidth
                    value={fichaAtual.conjugeCelular}
                    placeholder="(00) 00000-0000"
                    onChange={(e) => !ro && setField("conjugeCelular", mascararCelular(e.target.value))}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 15 } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.conjugeCelular))}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    label="E-mail"
                    size="small"
                    fullWidth
                    helperText={EMAIL_HELPER_TEXT}
                    value={fichaAtual.conjugeEmail}
                    onChange={(e) => !ro && setField("conjugeEmail", e.target.value)}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 500 } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.conjugeEmail))}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </>
      )}

      {/* Emprego */}
      <Accordion sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Emprego / Renda</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                label="Empresa onde Trabalha"
                size="small"
                fullWidth
                value={fichaAtual.pfEmpresaTrabalha}
                onChange={(e) => !ro && setField("pfEmpresaTrabalha", e.target.value.toUpperCase())}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 255 } }}
                sx={{
                  ...inputSx(ro, campoVazio(fichaAtual.pfEmpresaTrabalha), false),
                  "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Data de Admissão"
                type="date"
                size="small"
                fullWidth
                value={fichaAtual.pfEmpresaDtAdmissao}
                onChange={(e) => !ro && setField("pfEmpresaDtAdmissao", limitarAnoData(e.target.value))}
                slotProps={{ input: { readOnly: ro }, inputLabel: { shrink: true } }}
                sx={inputSx(ro, campoVazio(fichaAtual.pfEmpresaDtAdmissao), false)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Cargo/Função"
                size="small"
                fullWidth
                value={fichaAtual.pfEmpresaCargo}
                onChange={(e) => !ro && setField("pfEmpresaCargo", e.target.value.toUpperCase())}
                slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 100 } }}
                sx={{
                  ...inputSx(ro, campoVazio(fichaAtual.pfEmpresaCargo), false),
                  "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Salário (R$)"
                size="small"
                fullWidth
                value={fichaAtual.pfEmpresaSalario}
                onChange={(e) => !ro && setField("pfEmpresaSalario", maskMoeda(e.target.value))}
                slotProps={{ input: { readOnly: ro } }}
                sx={inputSx(ro, campoVazio(fichaAtual.pfEmpresaSalario), false)}
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2, borderColor: "var(--ig-b-333333)" }} />
          <Typography sx={{ color: "var(--ig-t-aaaaaa)", fontSize: "0.8rem", mb: 1 }}>Endereço da Empresa</Typography>
          <BlocoEndereco fields={FIELDS_END_PFEMP} form={fichaAtual} setField={setField} ro={ro} ehAprovador={ehAprovador} layout="largo" />
          <Divider sx={{ my: 2, borderColor: "var(--ig-b-333333)" }} />
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: "auto" }}>
              <FormControlLabel
                label={<Typography sx={{ color: "var(--ig-t-cccccc)" }}>Possui outro rendimento?</Typography>}
                control={
                  <Switch
                    checked={fichaAtual.pfEmpresaOutroRendimento === true}
                    onChange={(e) => !ro && setField("pfEmpresaOutroRendimento", e.target.checked)}
                    disabled={ro}
                    sx={{ "& .MuiSwitch-track": { backgroundColor: "#555" } }}
                  />
                }
              />
            </Grid>
            {fichaAtual.pfEmpresaOutroRendimento === true && (
              <>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="Valor (R$)"
                    size="small"
                    fullWidth
                    value={fichaAtual.pfEmpresaOutroRendimentoValor}
                    onChange={(e) => !ro && setField("pfEmpresaOutroRendimentoValor", maskMoeda(e.target.value))}
                    slotProps={{ input: { readOnly: ro } }}
                    sx={inputSx(ro, campoVazio(fichaAtual.pfEmpresaOutroRendimentoValor))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <TextField
                    label="Proveniente de"
                    size="small"
                    fullWidth
                    value={fichaAtual.pfEmpresaOutroRendimentoProvem}
                    onChange={(e) => !ro && setField("pfEmpresaOutroRendimentoProvem", e.target.value.toUpperCase())}
                    slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 255 } }}
                    sx={{
                      ...inputSx(ro, campoVazio(fichaAtual.pfEmpresaOutroRendimentoProvem)),
                      "& input": { color: "var(--ig-t-ffffff)", textTransform: "uppercase" },
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {fichaAtual.pfEstadoCivil && !ESTADOS_CIVIS_SEM_CONJUGE.includes(fichaAtual.pfEstadoCivil) && (
        <>
          {/* Emprego Cônjuge */}
          <Accordion sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
              <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Emprego / Renda do Cônjuge</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: "auto" }}>
                  <FormControlLabel
                    label={<Typography sx={{ color: "var(--ig-t-cccccc)" }}>Cônjuge possui renda?</Typography>}
                    control={
                      <Switch
                        checked={fichaAtual.conjugePossuiRenda === true}
                        onChange={(e) => !ro && setField("conjugePossuiRenda", e.target.checked)}
                        disabled={ro}
                        sx={{ "& .MuiSwitch-track": { backgroundColor: "#555" } }}
                      />
                    }
                  />
                </Grid>
              </Grid>
              {fichaAtual.conjugePossuiRenda === true && (
                <>
                  <Divider sx={{ my: 2, borderColor: "var(--ig-b-333333)" }} />
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <TextField
                        label="Empresa onde Trabalha"
                        size="small"
                        fullWidth
                        value={fichaAtual.conjugeEmpresaTrabalha}
                        onChange={(e) => !ro && setField("conjugeEmpresaTrabalha", e.target.value.toUpperCase())}
                        slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 255 } }}
                        sx={{
                          ...inputSx(ro, campoVazio(fichaAtual.conjugeEmpresaTrabalha)),
                          "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <TextField
                        label="Data de Admissão"
                        type="date"
                        size="small"
                        fullWidth
                        value={fichaAtual.conjugeEmpresaDtAdmissao}
                        onChange={(e) => !ro && setField("conjugeEmpresaDtAdmissao", limitarAnoData(e.target.value))}
                        slotProps={{ input: { readOnly: ro }, inputLabel: { shrink: true } }}
                        sx={inputSx(ro, campoVazio(fichaAtual.conjugeEmpresaDtAdmissao))}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Cargo/Função"
                        size="small"
                        fullWidth
                        value={fichaAtual.conjugeEmpresaCargo}
                        onChange={(e) => !ro && setField("conjugeEmpresaCargo", e.target.value.toUpperCase())}
                        slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 100 } }}
                        sx={{
                          ...inputSx(ro, campoVazio(fichaAtual.conjugeEmpresaCargo)),
                          "& input": { textTransform: "uppercase", color: "var(--ig-t-ffffff)" },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <TextField
                        label="Salário (R$)"
                        size="small"
                        fullWidth
                        value={fichaAtual.conjugeEmpresaSalario}
                        onChange={(e) => !ro && setField("conjugeEmpresaSalario", maskMoeda(e.target.value))}
                        slotProps={{ input: { readOnly: ro } }}
                        sx={inputSx(ro, campoVazio(fichaAtual.conjugeEmpresaSalario))}
                      />
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2, borderColor: "var(--ig-b-333333)" }} />
                  <Typography sx={{ color: "var(--ig-t-aaaaaa)", fontSize: "0.8rem", mb: 1 }}>Endereço da Empresa</Typography>
                  <BlocoEndereco fields={FIELDS_END_CJEMP} form={fichaAtual} setField={setField} ro={ro} ehAprovador={ehAprovador} layout="largo" />
                  <Divider sx={{ my: 2, borderColor: "var(--ig-b-333333)" }} />
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: "auto" }}>
                      <FormControlLabel
                        label={<Typography sx={{ color: "var(--ig-t-cccccc)" }}>Cônjuge possui outro rendimento?</Typography>}
                        control={
                          <Switch
                            checked={fichaAtual.conjugeEmpresaOutroRendimento === true}
                            onChange={(e) => !ro && setField("conjugeEmpresaOutroRendimento", e.target.checked)}
                            disabled={ro}
                            sx={{ "& .MuiSwitch-track": { backgroundColor: "#555" } }}
                          />
                        }
                      />
                    </Grid>
                    {fichaAtual.conjugeEmpresaOutroRendimento === true && (
                      <>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <TextField
                            label="Valor (R$)"
                            size="small"
                            fullWidth
                            value={fichaAtual.conjugeEmpresaOutroRendimentoValor}
                            onChange={(e) => !ro && setField("conjugeEmpresaOutroRendimentoValor", maskMoeda(e.target.value))}
                            slotProps={{ input: { readOnly: ro } }}
                            sx={inputSx(ro, campoVazio(fichaAtual.conjugeEmpresaOutroRendimentoValor))}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <TextField
                            label="Proveniente de"
                            size="small"
                            fullWidth
                            value={fichaAtual.conjugeEmpresaOutroRendimentoProvem}
                            onChange={(e) => !ro && setField("conjugeEmpresaOutroRendimentoProvem", e.target.value.toUpperCase())}
                            slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 255 } }}
                            sx={{
                              ...inputSx(ro, campoVazio(fichaAtual.conjugeEmpresaOutroRendimentoProvem)),
                              "& input": { color: "var(--ig-t-ffffff)", textTransform: "uppercase" },
                            }}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </>
              )}
            </AccordionDetails>
          </Accordion>
        </>
      )}

      {/* Checklist de Documentos PF */}
      <Accordion sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Checklist de Documentos</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1 }}>
          <ChecklistDocumentos
            checklist={checklistAtual}
            fichaId={fichaId}
            docs={docsPorFicha[fichaId] ?? EMPTY_DOCS}
            uploadState={uploadState[fichaId] ?? EMPTY_UPLOAD_STATE_FICHA}
            uploadando={uploadando}
            ro={ro}

            mostrarLinkGoogle={mostrarLinkGoogleChecklist}
            linkGoogleImovel={linkGoogleImovel}

            onChangeLinkGoogleImovel={(valor) => setField("linkGoogleImovel", valor)}

            observacaoDocumental={fichaAtual.observacaoDocumental ?? ""}
            onChangeObservacaoDocumental={(valor) => setField("observacaoDocumental", valor)}

            onSetUploadField={setUploadField}
            onUpload={handleUploadDoc}
            onDelete={handleDeleteDoc}
            onVisualizar={handleVisualizarDoc}
            onDownload={handleDownloadDoc}
          />
        </AccordionDetails>
      </Accordion>

      {fichaAtual.pfEstadoCivil && !ESTADOS_CIVIS_SEM_CONJUGE.includes(fichaAtual.pfEstadoCivil) && (
        <>
          {/* Checklist de Documentos do Cônjuge */}
          <Accordion sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
              <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Checklist de Documentos do Cônjuge (se aplicável)</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 1 }}>
              <ChecklistDocumentos
                checklist={CHECKLIST_CONJUGE_LOCATARIO}
                fichaId={fichaId}
                docs={docsPorFicha[fichaId] ?? EMPTY_DOCS}
                uploadState={uploadState[fichaId] ?? EMPTY_UPLOAD_STATE_FICHA}
                uploadando={uploadando}
                ro={ro}
                onSetUploadField={setUploadField}
                onUpload={handleUploadDoc}
                onDelete={handleDeleteDoc}
                onVisualizar={handleVisualizarDoc}
                onDownload={handleDownloadDoc}
              />
            </AccordionDetails>
          </Accordion>
        </>
      )}

      {/* Observação */}
      <Accordion sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>Observação</Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 2 }}>
          <TextField
            label="Observação"
            size="small"
            fullWidth
            multiline
            rows={4}
            value={fichaAtual.observacao ?? ""}
            onChange={(e) => !ro && setField("observacao", e.target.value.toUpperCase())}
            slotProps={{
              input: {
                readOnly: ro,
              },
            }}
            sx={{
              ...inputSx(ro, campoVazio(fichaAtual.observacao)),
              "& textarea": {
                color: "var(--ig-t-ffffff)",
                textTransform: "uppercase",
              },
            }}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  return fichaAtual.tipoPessoa === "PJ" ? renderFormPJ() : renderFormPF();
});
